import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Check } from "lucide-react";

function EnquiryForm({ type, fields, submitLabel }) {
  const [form, setForm] = useState({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) if (f.required && !form[f.key]) { toast.error(`Please enter ${f.label}`); return; }
    setBusy(true);
    try { await api.post("/enquiries", { type, data: form }); setDone(true); }
    catch { toast.error("Could not submit, try again."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="rounded-3xl border border-border p-10 text-center" data-testid="enquiry-success">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-mart-home/15"><Check className="h-7 w-7 text-mart-home" /></div>
      <h3 className="mt-4 text-2xl font-extrabold text-midnight">Thank you!</h3>
      <p className="mt-2 text-muted-foreground">We've received your details and our team will be in touch soon.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 rounded-3xl border border-border p-6 md:p-8" data-testid="enquiry-form">
      {fields.map((f) => (
        <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
          <label className="mb-1.5 block text-sm font-semibold text-midnight">{f.label}{f.required && " *"}</label>
          {f.type === "textarea" ? (
            <textarea value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={4} data-testid={`field-${f.key}`} className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          ) : f.type === "select" ? (
            <select value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold">
              <option value="">Select…</option>{f.options.map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : (
            <input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          )}
        </div>
      ))}
      <button disabled={busy} data-testid="enquiry-submit" className="sm:col-span-2 rounded-full bg-midnight py-3.5 font-bold text-white disabled:opacity-60">{busy ? "Submitting…" : submitLabel}</button>
    </form>
  );
}

function Hero({ tag, title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-[1000px] px-4 md:px-8 py-12">
      <Reveal>
        <span className="text-xs font-bold uppercase tracking-widest text-gold-dark">{tag}</span>
        <h1 className="mt-2 text-4xl font-extrabold text-midnight">{title}</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">{subtitle}</p>
      </Reveal>
      <div className="mt-8">{children}</div>
    </div>
  );
}

export function BecomeVendor() {
  return (
    <Hero tag="Vendor Ecosystem" title="Become a KRVMart Vendor"
      subtitle="KRVMart may work with manufacturers, local furniture makers, skilled carpenters, consumer product suppliers, fashion suppliers, electronics brands and distributors. Tell us about your business.">
      <EnquiryForm type="vendor" submitLabel="Submit Application" fields={[
        { key: "business_name", label: "Business Name", required: true },
        { key: "contact_person", label: "Contact Person", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", required: true },
        { key: "categories", label: "Product Categories", full: true, required: true },
        { key: "city", label: "City / Service Area" },
        { key: "capacity", label: "Manufacturing / Supply Capacity" },
        { key: "details", label: "Tell us about your products", type: "textarea", full: true },
      ]} />
    </Hero>
  );
}

export function FurnitureVendor() {
  return (
    <Hero tag="Wood Mart" title="Furniture Vendor Registration"
      subtitle="Register as an approved Wood Mart furniture vendor. Your wholesale pricing stays confidential — customers only see the KRVMart / Wood Mart brand.">
      <EnquiryForm type="furniture_vendor" submitLabel="Register as Furniture Vendor" fields={[
        { key: "business_name", label: "Business Name", required: true },
        { key: "contact_person", label: "Contact Person", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", required: true },
        { key: "product_categories", label: "Furniture Categories", full: true, required: true },
        { key: "materials", label: "Materials & Design Styles", full: true },
        { key: "service_area", label: "Service Area" },
        { key: "delivery_capability", label: "Delivery Capability" },
        { key: "customization", label: "Customization Capability", type: "select", options: ["Yes", "No", "Limited"] },
        { key: "warranty", label: "Warranty Offered" },
        { key: "details", label: "Additional details", type: "textarea", full: true },
      ]} />
    </Hero>
  );
}

export function Franchise() {
  return (
    <Hero tag="Expansion" title="Franchise With KRVMart"
      subtitle="KRVMart is being designed as a scalable multi-category retail concept capable of expanding across cities through standardized stores, category systems, technology and operational processes. Register your interest.">
      <EnquiryForm type="franchise" submitLabel="Register Your Interest" fields={[
        { key: "name", label: "Name", required: true },
        { key: "phone", label: "Phone", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "city", label: "City", required: true },
        { key: "investment_range", label: "Investment Range", type: "select", options: ["₹25L - ₹50L", "₹50L - ₹1Cr", "₹1Cr - ₹2Cr", "₹2Cr+"] },
        { key: "preferred_location", label: "Preferred Location" },
        { key: "experience", label: "Business Experience", type: "textarea", full: true },
      ]} />
    </Hero>
  );
}

export function Corporate() {
  return (
    <Hero tag="Corporate" title="Corporate Enquiries"
      subtitle="Bulk orders, corporate gifting, or partnership enquiries — our team will help.">
      <EnquiryForm type="corporate" submitLabel="Send Enquiry" fields={[
        { key: "company", label: "Company Name", required: true },
        { key: "name", label: "Contact Person", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", required: true },
        { key: "requirement", label: "Your Requirement", type: "textarea", full: true, required: true },
      ]} />
    </Hero>
  );
}

export function Contact() {
  return (
    <Hero tag="Support" title="How Can We Help?"
      subtitle="Reach us via WhatsApp, phone or email — or send a message below. We help with orders, returns, warranty, furniture installation and vendor enquiries.">
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[["WhatsApp", "+91 90000 00000"], ["Phone", "+91 90000 00000"], ["Email", "care@krvmart.com"]].map(([t, v]) => (
          <div key={t} className="rounded-2xl border border-border p-5"><p className="text-xs font-bold uppercase text-muted-foreground">{t}</p><p className="mt-1 font-bold text-midnight">{v}</p></div>
        ))}
      </div>
      <EnquiryForm type="contact" submitLabel="Send Message" fields={[
        { key: "name", label: "Name", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "subject", label: "Subject", full: true },
        { key: "message", label: "Message", type: "textarea", full: true, required: true },
      ]} />
    </Hero>
  );
}
